/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleCheck, X } from 'lucide-react';
import { Text, UnstyledButton } from '@/shared/ui';
import { SpreadsheetConfirmDialog } from './SpreadsheetConfirmDialog';
import type { ImportResultSummary } from './import-result-state';
import type { SpreadsheetEntity } from '../types';

export interface ImportResultBannerProps {
  entity: SpreadsheetEntity;
  summary: ImportResultSummary;
  /** Whether the listing is currently narrowed to this import. */
  onlyThisImport: boolean;
  onToggleOnlyThisImport: () => void;
  onUndo: () => void;
  onDismiss: () => void;
}

const actionStyle: React.CSSProperties = {
  fontSize: 'var(--text-body-sm)',
  fontWeight: 'var(--font-semibold)' as unknown as number,
  color: 'var(--seed-base)',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  background: 'transparent',
};

/**
 * The green strip that lands on the listing right after an import.
 *
 * It exists so the import does not end in silence: it says how many records
 * arrived, whether the brokers were told, and gives the two things someone
 * wants within the first ten seconds -- see only these, or take it all back.
 *
 * @param props - The result summary and the three actions.
 * @returns The rendered strip.
 */
export const ImportResultBanner: React.FC<ImportResultBannerProps> = ({
  entity, summary, onlyThisImport, onToggleOnlyThisImport, onUndo, onDismiss,
}) => {
  const { t } = useTranslation('features/spreadsheets');
  const [confirmingUndo, setConfirmingUndo] = useState(false);

  const titleKey = entity === 'clients' ? 'banner.titleClients' : 'banner.titleProperties';
  const subtitle = summary.status === 'processing'
    ? t('banner.processing')
    : t('banner.subNotNotified');

  return (
    <>
      <div
        data-testid="import-result-banner"
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--status-success-subtle)',
          border: '1px solid var(--status-success-border)',
          flexShrink: 0,
          fontFamily: 'var(--font-family)',
        }}
      >
        <CircleCheck width={18} height={18} style={{ color: 'var(--status-success-text)', flexShrink: 0 }} />
        <Text component="span" style={{ flex: 1, fontSize: 'var(--text-body-sm)', minWidth: 0 }}>
          <b>{t(titleKey, { count: summary.imported, file: summary.fileName })}</b>
          {' '}
          <Text as="span" style={{ color: 'var(--text-secondary)' }}>{subtitle}</Text>
        </Text>

        <UnstyledButton
          type="button"
          data-testid="import-banner-only-these"
          onClick={onToggleOnlyThisImport}
          style={actionStyle}
        >
          {t(onlyThisImport ? 'banner.seeAll' : 'banner.seeOnlyThese')}
        </UnstyledButton>

        {summary.undoable && (
          <UnstyledButton
            type="button"
            data-testid="import-banner-undo"
            onClick={() => setConfirmingUndo(true)}
            style={actionStyle}
          >
            {t('banner.undo')}
          </UnstyledButton>
        )}

        <UnstyledButton
          type="button"
          data-testid="import-banner-dismiss"
          aria-label={t('banner.dismiss')}
          onClick={onDismiss}
          style={{ ...actionStyle, color: 'var(--text-secondary)', display: 'inline-flex' }}
        >
          <X width={14} height={14} />
        </UnstyledButton>
      </div>

      <SpreadsheetConfirmDialog
        open={confirmingUndo}
        title={t('banner.undoTitle')}
        description={t('banner.undoDescription')}
        confirmLabel={t('banner.undoConfirm')}
        cancelLabel={t('banner.undoCancel')}
        onOpenChange={setConfirmingUndo}
        onConfirm={() => { setConfirmingUndo(false); onUndo(); }}
      />
    </>
  );
};
